import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {Box, Button, Card, CardContent, Dialog, Drawer, IconButton, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import {ICustomer} from '@mybake/ui/models/Customers/ICustomer';
import {ICustomerGuid} from '@mybake/ui/models/Customers/ICustomerGuid';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {ContentSkeleton} from '../common/loading/ContentSkeleton';
import {getCustomerName} from './order/orderDisplay';

interface Props {
    open: boolean;
    customers?: Array<ICustomer | ICustomerGuid>;
    selectedCustomerId?: number | string | null;
    isLoading?: boolean;
    onClose: () => void;
    onSelect: (customer: ICustomer | ICustomerGuid) => void;
}

export const CustomerPickerSheet = ({open, customers = [], selectedCustomerId, isLoading, onClose, onSelect}: Props) => {
    const {t} = useAppTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const content = (
        <>
            <Box sx={{width: 40, height: 4, borderRadius: 2, bgcolor: 'grey.400', mx: 'auto', mt: 1.25, display: {xs: 'block', sm: 'none'}}}/>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{px: {xs: 2, sm: 3}, pt: 1, pb: 1.5}}>
                <Box>
                    <Typography variant="h6" fontWeight={700}>{t('customers:customers')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('customers:found_customers')}</Typography>
                </Box>
                <IconButton aria-label={t('common:close')} onClick={onClose}><CloseIcon/></IconButton>
            </Stack>

            <Stack spacing={1} sx={{px: {xs: 1.25, sm: 2}, pb: 2, overflowY: 'auto', maxHeight: {xs: 'calc(78dvh - 86px)', sm: '56vh'}}}>
                {isLoading && <ContentSkeleton variant="inline"/>}
                {!isLoading && customers.map(customer => {
                    const isSelected = selectedCustomerId === customer.id;
                    const customerName = getCustomerName(
                        customer.firstName ?? undefined,
                        customer.lastName ?? undefined,
                        customer.name ?? undefined,
                        customer.phoneNumber ?? undefined,
                        customer.email ?? undefined,
                        '—',
                    );

                    return (
                        <Card key={customer.id} variant="outlined" sx={{borderRadius: 2.5, borderColor: isSelected ? 'primary.main' : 'divider'}}>
                            <CardContent sx={{p: 1.5, '&:last-child': {pb: 1.5}}}>
                                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                    <Box sx={{display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: '50%', bgcolor: 'rgba(154, 90, 60, 0.12)', color: 'primary.main', flexShrink: 0}}>
                                        <PersonOutlineIcon/>
                                    </Box>
                                    <Box minWidth={0} flexGrow={1}>
                                        <Typography fontWeight={700} noWrap>{customerName}</Typography>
                                        {customer.phoneNumber && <Stack direction="row" spacing={0.5} alignItems="center"><PhoneOutlinedIcon sx={{fontSize: 15}}/><Typography variant="body2" color="text.secondary" noWrap>{customer.phoneNumber}</Typography></Stack>}
                                        {customer.email && <Stack direction="row" spacing={0.5} alignItems="center"><EmailOutlinedIcon sx={{fontSize: 15}}/><Typography variant="body2" color="text.secondary" noWrap>{customer.email}</Typography></Stack>}
                                    </Box>
                                    <Button variant={isSelected ? 'outlined' : 'contained'} size="small" onClick={() => onSelect(customer)}>
                                        {isSelected ? t('customers:selected_customer') : t('common:select')}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })}
                {!isLoading && customers.length === 0 && <Typography color="text.secondary" textAlign="center" sx={{py: 3}}>{t('customers:customer_not_found')}</Typography>}
            </Stack>
        </>
    );

    return isMobile ? (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '100%',
                    maxWidth: 720,
                    mx: 'auto',
                    borderRadius: '24px 24px 0 0',
                    maxHeight: '78dvh',
                    backgroundImage: 'none',
                },
            }}
        >
            {content}
        </Drawer>
    ) : (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{sx: {borderRadius: 3, backgroundImage: 'none'}}}>
            {content}
        </Dialog>
    );
};
